import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Blobs, FloatingBubbles } from "../components/Effects";
import { Scale, FileText, CheckCircle2, AlertTriangle, ShieldCheck, Mail, Phone, Globe, Lock } from "lucide-react";

export default function TermsConditions() {
  const navigate = useNavigate();

  const handleBook = () => {
    navigate("/");
    setTimeout(() => {
      document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  return (
    <div className="min-h-screen bg-[#FFF8F2] text-woofsy-dark overflow-hidden">
      <Navbar onBook={handleBook} />

      {/* Background Decorative Effects */}
      <Blobs />
      <FloatingBubbles count={10} />

      {/* Header Section */}
      <section className="relative pt-32 pb-10 px-5 sm:px-8 max-w-4xl mx-auto text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-4 text-xs sm:text-sm font-semibold text-woofsy-dark shadow-sm border border-woofsy-primary/20">
            <Scale className="w-4 h-4 text-woofsy-primary" />
            <span>Woofsy Service Agreement & Legal Terms</span>
          </div>

          <h1 className="font-heading font-extrabold text-3xl sm:text-5xl text-woofsy-dark tracking-tight">
            Terms & <span className="text-gradient">Conditions</span>
          </h1>

          <p className="mt-3 text-sm sm:text-base font-semibold text-woofsy-primary">
            Effective Date: July 31, 2026
          </p>

          <p className="mt-4 text-sm sm:text-base text-woofsy-dark/75 max-w-2xl mx-auto leading-relaxed">
            These Terms & Conditions ("Terms") govern your use of the website, mobile application (if applicable), and services provided by Woofsy ("Company", "we", "our", or "us").
          </p>
        </motion.div>
      </section>

      {/* Main Content Box */}
      <section className="py-8 px-5 sm:px-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="glass rounded-[2rem] p-7 sm:p-12 shadow-xl border border-white/80 space-y-8 text-woofsy-dark/85 text-sm sm:text-base leading-relaxed bg-white/90"
        >
          <div className="border-b border-slate-200/80 pb-5">
            <p className="font-semibold text-woofsy-primary text-sm sm:text-base">
              By accessing our website or booking any service, you agree to these Terms.
            </p>
          </div>

          {/* 1. Eligibility */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">1.</span> Eligibility
            </h2>
            <p className="text-xs sm:text-sm">
              You must be at least 18 years of age and legally capable of entering into a binding contract under Indian law to use our services.
            </p>
          </div>

          {/* 2. Services */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">2.</span> Services
            </h2>
            <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm">
              <li>Woofsy provides doorstep pet grooming and related pet care services.</li>
              <li>Service availability may vary depending on your location in Pune.</li>
              <li>We reserve the right to refuse or discontinue services where necessary for safety, operational, or legal reasons.</li>
            </ul>
          </div>

          {/* 3. Booking */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">3.</span> Booking
            </h2>
            <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm">
              <li>Customers are responsible for providing accurate information while booking.</li>
              <li>Appointment confirmation is subject to groomer availability.</li>
              <li>Woofsy reserves the right to reschedule appointments due to severe weather, emergencies, operational issues, or unforeseen circumstances.</li>
            </ul>
          </div>

          {/* 4. Pricing */}
          <div className="space-y-3 p-5 rounded-2xl bg-orange-50/70 border border-orange-100">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">4.</span> Pricing
            </h2>
            <p className="text-xs sm:text-sm">
              All prices displayed are in Indian Rupees (INR). Prices shown on the website are starting prices. The final service charge may vary depending on:
            </p>
            <div className="grid sm:grid-cols-2 gap-2 text-xs sm:text-sm pt-1">
              {["Pet Breed", "Coat Condition & Matting", "Specific Grooming Requirements", "Pet Temperament & Handling", "Duration of Service"].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-white/90 p-2.5 rounded-xl border border-orange-200/60">
                  <CheckCircle2 className="w-4 h-4 text-woofsy-primary shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-600 italic">Any additional charges will be discussed with the customer before the grooming session begins.</p>
          </div>

          {/* 5. Customer Responsibilities */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">5.</span> Customer Responsibilities
            </h2>
            <p className="text-xs sm:text-sm">The customer agrees to:</p>
            <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm">
              <li>Provide accurate booking information.</li>
              <li>Ensure the pet is available at the scheduled time.</li>
              <li>Inform Woofsy about any medical conditions, allergies, injuries, or aggressive behaviour prior to the appointment.</li>
              <li>Ensure that a responsible adult is present during the entire grooming session.</li>
            </ul>
            <p className="text-xs text-amber-700 font-medium">Failure to disclose relevant information may affect the safety and quality of the service.</p>
          </div>

          {/* 6. Health & Safety */}
          <div className="space-y-3 p-5 rounded-2xl bg-rose-50/70 border border-rose-100">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-rose-600 font-black">6.</span> Health & Safety
            </h2>
            <p className="text-xs sm:text-sm">Woofsy reserves the right to refuse or discontinue grooming if:</p>
            <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-rose-950">
              <li>The pet is excessively aggressive or anxious.</li>
              <li>The pet appears seriously ill, injured, or in distress.</li>
              <li>Grooming may cause harm to the pet or the groomer.</li>
              <li>The pet has undisclosed contagious health conditions.</li>
            </ul>
            <p className="text-xs text-slate-600">If veterinary attention appears necessary, the grooming session will be stopped immediately.</p>
          </div>

          {/* 7. Payments */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">7.</span> Payments
            </h2>
            <p className="text-xs sm:text-sm">
              Payment is due immediately upon completion of the service unless otherwise agreed. Woofsy accepts payments through approved online payment gateways and other payment options available on the website.
            </p>
          </div>

          {/* 8. Cancellation & Rescheduling */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">8.</span> Cancellation & Rescheduling
            </h2>
            <p className="text-xs sm:text-sm">
              Customers may cancel or reschedule an appointment without charge if the request is made at least 24 hours before the scheduled time. Late cancellations or repeated no-shows may attract cancellation fees.
            </p>
          </div>

          {/* 9. Refunds */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">9.</span> Refunds
            </h2>
            <p className="text-xs sm:text-sm">
              Refunds, where applicable, will be processed in accordance with Woofsy's Refund & Cancellation Policy through original payment methods.
            </p>
          </div>

          {/* 10. Intellectual Property */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">10.</span> Intellectual Property
            </h2>
            <p className="text-xs sm:text-sm">
              All content on this website, including logos, graphics, images, text, videos, trademarks, and software, is owned by or licensed to Woofsy and protected under applicable intellectual property laws. No content may be copied, reproduced, modified, or distributed without prior written permission.
            </p>
          </div>

          {/* 11. Limitation of Liability */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">11.</span> Limitation of Liability
            </h2>
            <p className="text-xs sm:text-sm">
              Woofsy exercises reasonable care while providing its services. However, Woofsy shall not be liable for undisclosed medical conditions, allergic reactions not previously disclosed, pre-existing health issues, or behavioural incidents involving the pet.
            </p>
          </div>

          {/* 12. Privacy */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">12.</span> Privacy
            </h2>
            <p className="text-xs sm:text-sm">
              Your personal information will be collected, stored, and processed strictly in accordance with Woofsy's Privacy Policy.
            </p>
          </div>

          {/* 13. Website Use */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">13.</span> Website Use
            </h2>
            <p className="text-xs sm:text-sm">
              You agree not to use the website for unlawful purposes, attempt unauthorized access to our systems, upload malicious software, misrepresent your identity, or interfere with security features.
            </p>
          </div>

          {/* 14. Force Majeure */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">14.</span> Force Majeure
            </h2>
            <p className="text-xs sm:text-sm">
              Woofsy shall not be liable for delays or failure to perform obligations caused by events beyond its reasonable control, including natural disasters, government restrictions, strikes, pandemics, power failures, or internet disruptions.
            </p>
          </div>

          {/* 15. Governing Law & Jurisdiction */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">15.</span> Governing Law & Jurisdiction
            </h2>
            <p className="text-xs sm:text-sm">
              These Terms shall be governed by the laws of India. Any dispute arising out of or relating to these Terms shall be subject to the exclusive jurisdiction of the competent courts located in Pune, Maharashtra, India.
            </p>
          </div>

          {/* 16. Changes to the Terms */}
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-woofsy-dark flex items-center gap-2">
              <span className="text-woofsy-primary font-black">16.</span> Changes to the Terms
            </h2>
            <p className="text-xs sm:text-sm">
              Woofsy may update these Terms from time to time. Revised Terms will be published on the website and will become effective from the date of publication.
            </p>
          </div>

          {/* 17. Contact Information */}
          <div className="p-6 rounded-3xl bg-sky-50/80 border border-sky-100 space-y-3">
            <h2 className="font-heading font-bold text-lg text-woofsy-dark flex items-center gap-2">
              <span className="text-sky-600 font-black">17.</span> Contact Information
            </h2>
            <p className="text-xs sm:text-sm text-woofsy-dark/80">
              For questions regarding these Terms & Conditions, please contact us:
            </p>
            <div className="grid sm:grid-cols-3 gap-3 text-xs sm:text-sm pt-1">
              <a href="mailto:info@woofsy.co.in" className="flex items-center gap-2 p-3 rounded-2xl bg-white border border-sky-200/60 hover:text-sky-600 transition-colors">
                <Mail className="w-4 h-4 text-sky-500" />
                <span>info@woofsy.co.in</span>
              </a>
              <a href="tel:+918421114134" className="flex items-center gap-2 p-3 rounded-2xl bg-white border border-sky-200/60 hover:text-sky-600 transition-colors">
                <Phone className="w-4 h-4 text-sky-500" />
                <span>+91-8421114134</span>
              </a>
              <a href="https://www.woofsy.co.in" target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 rounded-2xl bg-white border border-sky-200/60 hover:text-sky-600 transition-colors">
                <Globe className="w-4 h-4 text-sky-500" />
                <span>www.woofsy.co.in</span>
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer onBook={handleBook} />
    </div>
  );
}
